/**
 * OCS Tooltip — mobile click support with close button.
 *
 * Desktop: CSS hover/focus handles visibility (no JS needed).
 * Mobile:  JS toggles `.is-active` on `.ocs-tooltip-wrapper` on trigger click.
 *          A close × button is injected into each tooltip so the user can
 *          dismiss it without tapping somewhere else.
 *
 * The cart drawer is re-rendered through Shopify section rendering, so this
 * module is intentionally idempotent and observes new tooltip markup after
 * morphSection swaps drawer/cart HTML.
 */
(function () {
  'use strict';

  const WRAPPER_SELECTOR = '.ocs-tooltip-wrapper';
  const TRIGGER_SELECTOR = '.ocs-tooltip-trigger';
  const TOOLTIP_SELECTOR = '.ocs-tooltip';
  const CLOSE_SELECTOR = '.ocs-tooltip-close';
  const MOBILE_QUERY = '(max-width: 749px)';
  const VIEWPORT_GUTTER = 8;
  const closeLabel = document.currentScript?.dataset?.closeLabel || 'Close tooltip';

  function resetTooltipViewportPosition(wrapper) {
    wrapper.querySelector(TOOLTIP_SELECTOR)?.style.removeProperty('--ocs-tooltip-viewport-offset-x');
  }

  function positionTooltipInViewport(wrapper) {
    const tooltip = wrapper.querySelector(TOOLTIP_SELECTOR);
    if (!tooltip) return;

    resetTooltipViewportPosition(wrapper);
    if (!window.matchMedia(MOBILE_QUERY).matches) return;

    const rect = tooltip.getBoundingClientRect();
    const viewportRight = window.innerWidth - VIEWPORT_GUTTER;
    const feedbackButton = document.querySelector('.ocs-feedback-tab');
    let viewportLeft = VIEWPORT_GUTTER;
    let offset = 0;

    if (feedbackButton) {
      const feedbackRect = feedbackButton.getBoundingClientRect();
      const feedbackVisible = getComputedStyle(feedbackButton).display !== 'none' &&
        getComputedStyle(feedbackButton).visibility !== 'hidden';
      const overlapsVertically = rect.bottom > feedbackRect.top && rect.top < feedbackRect.bottom;
      const alwaysAvoidFeedback = wrapper.classList.contains('ocs-tooltip-wrapper--ontario-grown');
      if (feedbackVisible && (alwaysAvoidFeedback || overlapsVertically)) {
        viewportLeft = Math.max(viewportLeft, feedbackRect.right + VIEWPORT_GUTTER);
      }
    }

    if (rect.left < viewportLeft) {
      offset = viewportLeft - rect.left;
    } else if (rect.right > viewportRight) {
      offset = viewportRight - rect.right;
    }

    if (offset) tooltip.style.setProperty('--ocs-tooltip-viewport-offset-x', `${offset}px`);
  }

  function scheduleTooltipViewportPosition(wrapper) {
    requestAnimationFrame(function () {
      if (wrapper.classList.contains('is-active')) positionTooltipInViewport(wrapper);
    });
  }

  function positionActiveTooltips() {
    document.querySelectorAll(`${WRAPPER_SELECTOR}.is-active`).forEach(positionTooltipInViewport);
  }

  function closeTooltip(wrapper) {
    resetTooltipViewportPosition(wrapper);
    wrapper.classList.remove('is-active');
    wrapper.querySelector(TRIGGER_SELECTOR)?.setAttribute('aria-expanded', 'false');
  }

  function closeAllTooltips(exceptWrapper) {
    document.querySelectorAll(`${WRAPPER_SELECTOR}.is-active`).forEach(function (wrapper) {
      if (wrapper !== exceptWrapper) closeTooltip(wrapper);
    });
  }

  function ensureCloseButton(wrapper) {
    const tooltip = wrapper.querySelector(TOOLTIP_SELECTOR);
    if (!tooltip || tooltip.querySelector(CLOSE_SELECTOR)) return;

    const closeBtn = document.createElement('button');
    closeBtn.type = 'button';
    closeBtn.className = 'ocs-tooltip-close';
    closeBtn.setAttribute('aria-label', closeLabel);
    closeBtn.innerHTML =
      '<svg viewBox="0 0 24 24" width="12" height="12" fill="currentColor" aria-hidden="true">' +
      '<path d="M5.293 6.707l5.293 5.293-5.293 5.293c-.391.391-.391 1.024 0 1.414s1.024.391 1.414 0' +
      'l5.293-5.293 5.293 5.293c.391.391 1.024.391 1.414 0s.391-1.024 0-1.414l-5.293-5.293 ' +
      '5.293-5.293c.391-.391.391-1.024 0-1.414s-1.024-.391-1.414 0l-5.293 5.293-5.293-5.293' +
      'c-.391-.391-1.024-.391-1.414 0s-.391 1.024 0 1.414z"/>' +
      '</svg>';
    tooltip.appendChild(closeBtn);
  }

  function initTooltips(root) {
    const scope = root instanceof Element || root instanceof Document ? root : document;
    const wrappers = [
      ...(scope instanceof Element && scope.matches(WRAPPER_SELECTOR) ? [scope] : []),
      ...scope.querySelectorAll(WRAPPER_SELECTOR),
    ];

    wrappers.forEach(function (wrapper) {
      const tooltip = wrapper.querySelector(TOOLTIP_SELECTOR);
      const trigger = wrapper.querySelector(TRIGGER_SELECTOR);
      if (!tooltip || !trigger) return;

      ensureCloseButton(wrapper);
      trigger.setAttribute('aria-expanded', wrapper.classList.contains('is-active') ? 'true' : 'false');
    });
  }

  function onDocumentClick(event) {
    const closeBtn = event.target.closest?.(CLOSE_SELECTOR);
    if (closeBtn) {
      event.stopPropagation();
      const wrapper = closeBtn.closest(WRAPPER_SELECTOR);
      if (wrapper) closeTooltip(wrapper);
      return;
    }

    const trigger = event.target.closest?.(TRIGGER_SELECTOR);
    if (trigger) {
      const wrapper = trigger.closest(WRAPPER_SELECTOR);
      if (!wrapper) return;

      event.stopPropagation();
      ensureCloseButton(wrapper);

      const isActive = wrapper.classList.contains('is-active');
      closeAllTooltips(wrapper);

      if (isActive) {
        closeTooltip(wrapper);
      } else {
        wrapper.classList.add('is-active');
        trigger.setAttribute('aria-expanded', 'true');
        scheduleTooltipViewportPosition(wrapper);
      }
      return;
    }

    if (!event.target.closest?.(WRAPPER_SELECTOR)) {
      closeAllTooltips();
    }
  }

  function onDocumentKeydown(event) {
    if (event.key !== 'Escape') return;

    closeAllTooltips();
    const focusedTrigger = document.querySelector(`${TRIGGER_SELECTOR}:focus`);
    if (focusedTrigger) focusedTrigger.blur();
  }

  function startTooltipSupport() {
    initTooltips(document);
    document.addEventListener('click', onDocumentClick);
    document.addEventListener('keydown', onDocumentKeydown);
    window.addEventListener('resize', positionActiveTooltips, { passive: true });
    window.visualViewport?.addEventListener('resize', positionActiveTooltips, { passive: true });

    if ('MutationObserver' in window) {
      new MutationObserver(function (mutations) {
        mutations.forEach(function (mutation) {
          mutation.addedNodes.forEach(function (node) {
            if (node instanceof Element) initTooltips(node);
          });
        });
      }).observe(document.body, { childList: true, subtree: true });
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', startTooltipSupport, { once: true });
  } else {
    startTooltipSupport();
  }
})();
